import AuthLayout from "layout/AuthLayout";
import { message, Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getLoanProfile, reapplyForLoan } from "services/LoanService";

export function ReapplyFormPage() {
  const navigate = useNavigate();
  const { loanType = "undergraduate" } = useParams();

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loanProfile, setLoanProfile] = useState(null);

  const [showPreview, setShowPreview] = useState(false);

  const [form, setForm] = useState({
    amount: "",
    purpose: "",
    loan_tenure: "12",
  });

  const fetchLoanProfile = useCallback(async () => {
    setLoadingProfile(true);

    try {
      const res = await getLoanProfile();

      const profile = res?.data?.data;

      if (!profile) {
        message.error(
          "Your loan profile could not be found. Please complete a full loan application first.",
        );
        return;
      }

      setLoanProfile(profile);
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to retrieve your loan profile.",
      );
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  useEffect(() => {
    fetchLoanProfile();
  }, [fetchLoanProfile]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const monthlyRepayment = useMemo(() => {
    const amount = Number(form.amount);
    const tenure = Number(form.loan_tenure);

    if (!amount || !tenure) {
      return 0;
    }

    return amount / tenure;
  }, [form.amount, form.loan_tenure]);

  const formatAmount = (amount) => {
    if (!amount) return "0";

    return Number(amount).toLocaleString("en-NG", {
      maximumFractionDigits: 2,
    });
  };

  /**
   * This only validates the form and moves to the preview.
   *
   * IMPORTANT:
   * No API request happens here.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.amount) {
      message.error("Please enter the amount you need.");
      return;
    }

    if (Number(form.amount) <= 0) {
      message.error("Loan amount must be greater than zero.");
      return;
    }

    if (!form.purpose.trim()) {
      message.error("Please enter the purpose of the loan.");
      return;
    }

    if (!form.loan_tenure) {
      message.error("Please select a loan tenure.");
      return;
    }

    setShowPreview(true);
  };

  /**
   * This is the actual application submission.
   *
   * It only happens AFTER the applicant has reviewed
   * and confirmed the preview.
   */
  const handleConfirmApplication = async () => {
    setLoadingSubmit(true);

    try {
      const payload = {
        amount: form.amount,
        purpose: form.purpose,
        loan_tenure: form.loan_tenure,
        loan_type: loanType,
      };

      const res = await reapplyForLoan(payload);

      const loan = res?.data?.data;

      if (!loan) {
        message.error("Unable to create your loan application.");
        return;
      }

      message.success("Your loan application has been created.");

      /**
       * Loan has now been created.
       *
       * Only now do we navigate to the payment stage.
       */
      if (loan.id) {
        navigate(`/dashboard/reapply/${loan.id}/payment`);
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to create your loan application.",
      );
    } finally {
      setLoadingSubmit(false);
    }
  };

  const institution = loanProfile?.institution || {};
  const guarantorOne = loanProfile?.guarantor_one || {};
  const guarantorTwo = loanProfile?.guarantor_two || {};

  const savedDocuments = [
    loanProfile?.admission_letter,
    loanProfile?.school_fee_receipt,
    loanProfile?.certificate,
    loanProfile?.othercertificate,
  ].filter(Boolean).length;

  const hasGuarantors =
    Boolean(guarantorOne?.first_name) || Boolean(guarantorTwo?.first_name);

  if (loadingProfile) {
    return (
      <AuthLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Spin size="large" />

            <p className="text-sm text-gray-500 font-poppins">
              Loading your loan profile...
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (!loanProfile) {
    return (
      <AuthLayout>
        <div className="max-w-xl mx-auto py-16 px-4">
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
              <i className="bi bi-person-vcard text-2xl text-gray-500" />
            </div>

            <h2 className="mt-4 text-lg font-semibold text-gray-800 font-poppins">
              Loan Profile Not Found
            </h2>

            <p className="mt-2 text-sm text-gray-500 font-poppins">
              You need to complete a full loan application before you can submit
              a recurring application.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  /**
   * ---------------------------------------------------------
   * PREVIEW
   * ---------------------------------------------------------
   *
   * This is shown BEFORE the loan is created.
   */
  if (showPreview) {
    return (
      <AuthLayout>
        <LoanApplicationPreview
          profile={loanProfile}
          application={{
            ...form,
            monthly_repayment: monthlyRepayment,
          }}
          onBack={() => setShowPreview(false)}
          onContinue={handleConfirmApplication}
          loading={loadingSubmit}
        />
      </AuthLayout>
    );
  }

  /**
   * ---------------------------------------------------------
   * FORM
   * ---------------------------------------------------------
   */
  return (
    <AuthLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 pb-24 font-poppins">
        {/* HEADER */}
        <header className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <span>Loan Application</span>

            <i className="bi bi-chevron-right text-xs" />

            <span className="text-primary-700 font-medium">Re-application</span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            Apply for a New Loan
          </h1>

          <p className="text-sm text-gray-500 mt-2 max-w-2xl">
            Your saved loan information will be reused for this application. You
            only need to provide the details that change with each application.
          </p>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* LOAN PROFILE */}
            <section>
              <div className="mb-3">
                <h2 className="text-base font-semibold text-gray-800">
                  Your Loan Profile
                </h2>

                <p className="text-xs text-gray-500 mt-1">
                  These details will be copied into your new application.
                </p>
              </div>

              <div className="border border-gray-200 rounded-xl bg-gray-50 p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                      <i className="bi bi-person-vcard text-lg" />
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        Saved Information
                      </p>

                      <p className="text-xs text-gray-500 mt-1">
                        Information from your previous application.
                      </p>
                    </div>
                  </div>

                  <Link
                    to="/dashboard/loan-profile"
                    className="text-xs font-semibold text-primary-700 hover:underline whitespace-nowrap"
                  >
                    Edit Profile
                  </Link>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                  <ProfilePreviewItem
                    label="Institution"
                    value={institution.name}
                  />

                  <ProfilePreviewItem
                    label="Course"
                    value={institution.course}
                  />

                  <ProfilePreviewItem
                    label="Guarantors"
                    value={hasGuarantors ? "Saved" : "Not provided"}
                  />

                  <ProfilePreviewItem
                    label="Documents"
                    value={`${savedDocuments} saved`}
                  />
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between gap-4">
                  <p className="text-[11px] text-gray-400">
                    Something has changed since your last application?
                  </p>

                  <Link
                    to="/dashboard/loan-profile"
                    className="text-xs font-semibold text-primary-700 flex items-center gap-1 whitespace-nowrap"
                  >
                    Manage profile
                    <i className="bi bi-arrow-right" />
                  </Link>
                </div>
              </div>
            </section>

            {/* APPLICATION DETAILS */}
            <section>
              <div className="mb-4">
                <h2 className="text-base font-semibold text-gray-800">
                  Application Details
                </h2>

                <p className="text-xs text-gray-500 mt-1">
                  These details are specific to this loan application.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6">
                <div className="space-y-5">
                  {/* AMOUNT */}
                  <div>
                    <label
                      htmlFor="amount"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      How much do you need?
                      <span className="text-red-500 ml-1">*</span>
                    </label>

                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        ₦
                      </span>

                      <input
                        id="amount"
                        name="amount"
                        type="number"
                        min="1"
                        step="1"
                        value={form.amount}
                        onChange={handleChange}
                        placeholder="200,000"
                        className="w-full pl-8 pr-4 py-3 text-sm border border-gray-300 rounded-lg outline-none focus:border-primary-600 focus:ring-1 focus:ring-primary-600"
                      />
                    </div>
                  </div>

                  {/* PURPOSE */}
                  <div>
                    <label
                      htmlFor="purpose"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Purpose of loan
                      <span className="text-red-500 ml-1">*</span>
                    </label>

                    <textarea
                      id="purpose"
                      name="purpose"
                      value={form.purpose}
                      onChange={handleChange}
                      maxLength={150}
                      rows={3}
                      placeholder="What will you use the loan for?"
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg outline-none resize-none focus:border-primary-600 focus:ring-1 focus:ring-primary-600"
                    />

                    <div className="flex justify-end mt-1">
                      <span className="text-[11px] text-gray-400">
                        {form.purpose.length}/150
                      </span>
                    </div>
                  </div>

                  {/* TENURE */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Loan Tenure
                      <span className="text-red-500 ml-1">*</span>
                    </label>

                    <div className="grid grid-cols-3 gap-3">
                      {["12", "24", "36"].map((tenure) => {
                        const selected = form.loan_tenure === tenure;

                        return (
                          <label
                            key={tenure}
                            className={`cursor-pointer rounded-lg border p-4 text-center transition ${
                              selected
                                ? "border-primary-600 bg-primary-50"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          >
                            <input
                              type="radio"
                              name="loan_tenure"
                              value={tenure}
                              checked={selected}
                              onChange={handleChange}
                              className="sr-only"
                            />

                            <span
                              className={`block text-sm font-semibold ${
                                selected ? "text-primary-700" : "text-gray-700"
                              }`}
                            >
                              {tenure} months
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* REPAYMENT PREVIEW */}
                  {monthlyRepayment > 0 && (
                    <div className="rounded-lg bg-primary-50 border border-primary-100 p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                          <i className="bi bi-calculator text-primary-700" />
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">
                            Estimated monthly repayment
                          </p>

                          <p className="text-lg font-semibold text-primary-800 mt-1">
                            ₦{formatAmount(monthlyRepayment)}
                          </p>

                          <p className="text-[11px] text-gray-500 mt-1">
                            Based on ₦{formatAmount(form.amount)} over{" "}
                            {form.loan_tenure} months.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* INFORMATION NOTICE */}
            <section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start gap-3">
                <i className="bi bi-info-circle text-primary-700 mt-0.5" />

                <div>
                  <p className="text-sm font-medium text-gray-700">
                    About your application
                  </p>

                  <p className="text-xs text-gray-500 mt-1 leading-5">
                    Your saved profile will be copied into this new application.
                    You can review the complete application before it is
                    submitted.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* FOOTER */}
          <footer className="mt-8 pt-5 border-t border-gray-200 flex items-center justify-between gap-3">
            <Link
              to="/dashboard/loan-profile"
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              View Loan Profile
            </Link>

            <button
              type="submit"
              disabled={loadingSubmit}
              className="px-6 py-2.5 rounded-lg bg-primary-800 text-white text-sm font-medium hover:bg-primary-900 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Review Application
              <i className="bi bi-arrow-right" />
            </button>
          </footer>
        </form>
      </div>
    </AuthLayout>
  );
}

/**
 * =========================================================
 * PREVIEW COMPONENT
 * =========================================================
 */

function LoanApplicationPreview({
  profile,
  application,
  onBack,
  onContinue,
  loading = false,
}) {
  const bank = profile?.bank_account || {};
  const institution = profile?.institution || {};

  const formatMoney = (value) => {
    if (!value) return "₦0";

    return `₦${Number(value).toLocaleString("en-NG", {
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 pb-24 font-poppins">
      {/* HEADER */}
      <header className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
          <span>Loan Application</span>

          <i className="bi bi-chevron-right text-xs" />

          <span className="text-primary-700 font-medium">Review</span>
        </div>

        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
          Review Your Loan Application
        </h1>

        <p className="text-sm text-gray-500 mt-2 max-w-2xl">
          Please review the information below carefully. Your loan has not been
          submitted yet.
        </p>
      </header>

      {/* PROFILE NOTICE */}
      <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 mb-6 flex gap-3">
        <div className="text-primary-700 mt-0.5">
          <i className="bi bi-info-circle" />
        </div>

        <div>
          <p className="text-sm font-semibold text-primary">
            Information from your Loan Profile
          </p>

          <p className="text-xs text-gray-600 mt-1">
            The information below will be copied into this application. If
            anything needs to be changed, you can update your Loan Profile
            before continuing.
          </p>

          <Link
            to="/dashboard/loan-profile"
            className="inline-block mt-2 text-xs font-semibold text-primary-700 underline"
          >
            Edit Loan Profile
          </Link>
        </div>
      </div>

      <div className="space-y-5">
        {/* NEW APPLICATION */}
        <PreviewSection title="New Application">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <PreviewField
              label="Loan Amount"
              value={formatMoney(application?.amount)}
            />

            <PreviewField label="Loan Purpose" value={application?.purpose} />

            <PreviewField
              label="Loan Tenure"
              value={
                application?.loan_tenure
                  ? `${application.loan_tenure} months`
                  : null
              }
            />

            <PreviewField
              label="Estimated Monthly Repayment"
              value={formatMoney(application?.monthly_repayment)}
            />
          </div>
        </PreviewSection>

        {/* BANK */}
        <PreviewSection title="Bank Account">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <PreviewField label="Account Name" value={bank.account_name} />

            <PreviewField label="Account Number" value={bank.account_number} />

            <PreviewField label="Bank" value={bank.bank_name} />

            <PreviewField label="Bank Code" value={bank.bank_code} />
          </div>
        </PreviewSection>

        {/* VERIFICATION */}
        <PreviewSection title="Verification & Employment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <PreviewField label="BVN" value={profile.bvn} />

            <PreviewField
              label="Employment Status"
              value={profile.employment_status}
            />

            <PreviewField label="Certificate" value={profile.certificate} />

            {profile.othercertificate && (
              <PreviewField
                label="Other Certificate"
                value={profile.othercertificate}
              />
            )}
          </div>
        </PreviewSection>

        {/* INSTITUTION */}
        {institution && (
          <PreviewSection title="Institution">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <PreviewField label="Institution" value={institution.name} />

              <PreviewField label="Course" value={institution.course} />

              <PreviewField label="Faculty" value={institution.faculty} />

              <PreviewField
                label="Matric Number"
                value={institution.matric_number}
              />

              <PreviewField
                label="Current Study Year"
                value={institution.current_study_year}
              />

              <PreviewField
                label="Graduation Year"
                value={institution.graduation_year}
              />
            </div>
          </PreviewSection>
        )}

        {/* GUARANTORS */}
        <PreviewSection title="Guarantors">
          <div className="space-y-5">
            <PreviewGuarantor
              title="Guarantor One"
              guarantor={profile.guarantor_one}
            />

            <PreviewGuarantor
              title="Guarantor Two"
              guarantor={profile.guarantor_two}
            />
          </div>
        </PreviewSection>

        {/* DOCUMENTS */}
        <PreviewSection title="Documents">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <PreviewDocument
              label="Admission Letter"
              url={profile.admission_letter}
            />

            <PreviewDocument
              label="School Fee Receipt"
              url={profile.school_fee_receipt}
            />

            {profile.certificate && (
              <PreviewDocument label="Certificate" url={profile.certificate} />
            )}

            {profile.othercertificate && (
              <PreviewDocument
                label="Other Certificate"
                url={profile.othercertificate}
              />
            )}
          </div>
        </PreviewSection>
      </div>

      {/* ACTIONS */}
      <footer className="mt-8 pt-5 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-end gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          className="px-6 py-2.5 text-sm font-medium text-primary-700 border border-primary-200 rounded-full text-center hover:bg-primary-50 disabled:opacity-60"
        >
          Back to Application
        </button>

        <button
          type="button"
          onClick={onContinue}
          disabled={loading}
          className="px-7 py-2.5 text-sm font-medium bg-secondary-700 text-white rounded-full disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Spin size="small" />
              Creating Application...
            </>
          ) : (
            <>
              Confirm & Continue
              <i className="bi bi-arrow-right" />
            </>
          )}
        </button>
      </footer>
    </div>
  );
}

/**
 * =========================================================
 * PREVIEW HELPERS
 * =========================================================
 */

function PreviewSection({ title, children }) {
  return (
    <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <header className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h3 className="font-poppins font-semibold text-primary text-base">
          {title}
        </h3>
      </header>

      <div className="p-5">{children}</div>
    </section>
  );
}

function PreviewField({ label, value, full = false }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <p className="text-xs text-gray-500 mb-1 font-poppins">{label}</p>

      <p className="text-sm font-medium text-gray-800 font-poppins break-words">
        {value || "Not provided"}
      </p>
    </div>
  );
}

function PreviewDocument({ label, url }) {
  if (!url) {
    return (
      <div>
        <p className="text-xs text-gray-500 mb-1 font-poppins">{label}</p>

        <p className="text-sm font-medium text-gray-400 font-poppins">
          Not uploaded
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-gray-500 mb-2 font-poppins">{label}</p>

      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-primary-700 hover:bg-gray-50"
      >
        <i className="bi bi-file-earmark-text" />

        <span>View document</span>

        <i className="bi bi-box-arrow-up-right text-xs" />
      </a>
    </div>
  );
}

function PreviewGuarantor({ title, guarantor }) {
  return (
    <div className="border border-gray-100 rounded-lg p-4">
      <h4 className="font-poppins font-medium text-sm text-primary mb-4">
        {title}
      </h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <PreviewField label="First Name" value={guarantor?.first_name} />

        <PreviewField label="Middle Name" value={guarantor?.middle_name} />

        <PreviewField label="Surname" value={guarantor?.surname} />

        <PreviewField label="Phone" value={guarantor?.phone} />

        <PreviewField label="Email" value={guarantor?.email} />

        <PreviewField label="Work Place" value={guarantor?.work_place} />

        <PreviewField label="NIN" value={guarantor?.nin} />
      </div>
    </div>
  );
}

function ProfilePreviewItem({ label, value }) {
  return (
    <div className="bg-white rounded-lg border border-gray-100 p-3">
      <p className="text-[11px] text-gray-400">{label}</p>

      <p
        className="text-xs font-medium text-gray-700 mt-1 truncate"
        title={value || ""}
      >
        {value || "Not provided"}
      </p>
    </div>
  );
}

export default ReapplyFormPage;
